import { isChangeOrigin } from '@tiptap/extension-collaboration';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReplaceStep } from '@tiptap/pm/transform';
import { SPLIT_VIEW_CARET_META_KEY } from '../zyversion-extension/zyversion-splitviewcaret-extension';

export const scrollHandlerPluginKey = new PluginKey('scrollHandler');

function isFullDocumentReplacement(tr: Transaction, oldDocSize: number): boolean {
    if (tr.steps.length !== 1) {
        return false;
    }

    const step = tr.steps[0];
    if (!(step instanceof ReplaceStep)) {
        return false;
    }

    return step.from === 0 && step.to === oldDocSize && step.slice.content.size > 0;
}

export function transactionInsertsContent(tr: Transaction, startDoc: PMNode): boolean {
    if (!tr.docChanged) {
        return false;
    }

    const endDoc = tr.doc;
    return endDoc.content.size > startDoc.content.size || endDoc.textContent.length > startDoc.textContent.length;
}

export function shouldSkipScrollTransaction(tr: Transaction, oldDocSize?: number): boolean {
    if (tr.getMeta(SPLIT_VIEW_CARET_META_KEY)) {
        return false;
    }

    if (!tr.docChanged) {
        return true;
    }
    if (isChangeOrigin(tr)) {
        return true;
    }
    if (tr.getMeta('y-sync$')) {
        return true;
    }
    if (tr.getMeta(scrollHandlerPluginKey)) {
        return true;
    }
    if (tr.getMeta('__uniqueIDTransaction')) {
        return true;
    }
    if (oldDocSize !== undefined && isFullDocumentReplacement(tr, oldDocSize)) {
        return true;
    }

    return false;
}

export function createScrollHandlerPlugin(): Plugin {
    return new Plugin({
        key: scrollHandlerPluginKey,

        appendTransaction(transactions, oldState, newState) {
            if (!transactions.some(tr => tr.docChanged)) {
                return null;
            }

            const oldDocSize = oldState.doc.content.size;

            if (transactions.some(tr => shouldSkipScrollTransaction(tr, oldDocSize))) {
                return null;
            }

            if (transactions.some(tr => tr.scrolledIntoView)) {
                return null;
            }

            if (!transactions.some(tr => transactionInsertsContent(tr, oldState.doc))) {
                return null;
            }

            return newState.tr.scrollIntoView().setMeta(scrollHandlerPluginKey, true);
        },
    });
}
